'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

const GlobalCommunitySection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  // Connection points on the world map (approximate lat/lng to SVG coords)
  const connectionPoints = [
    { x: 22, y: 38, label: 'Spain', delay: 0 },
    { x: 28, y: 42, label: 'France', delay: 200 },
    { x: 18, y: 55, label: 'Mexico', delay: 400 },
    { x: 22, y: 62, label: 'Colombia', delay: 600 },
    { x: 28, y: 68, label: 'Brazil', delay: 800 },
    { x: 24, y: 72, label: 'Argentina', delay: 1000 },
    { x: 50, y: 35, label: 'Germany', delay: 300 },
    { x: 55, y: 32, label: 'Russia', delay: 500 },
    { x: 68, y: 40, label: 'India', delay: 700 },
    { x: 80, y: 38, label: 'China', delay: 900 },
    { x: 88, y: 55, label: 'Australia', delay: 1100 },
    { x: 45, y: 48, label: 'Egypt', delay: 400 },
    { x: 48, y: 55, label: 'Kenya', delay: 600 },
    { x: 15, y: 45, label: 'USA', delay: 200 },
    { x: 12, y: 50, label: 'Canada', delay: 100 },
  ];

  const stats = [
    { value: '30+', key: 'stat1' },
    { value: '847+', key: 'stat2' },
    { value: '12,500+', key: 'stat3' },
  ];

  return (
    <section ref={sectionRef} id="comunidad" className="py-32 bg-[#0A1628] overflow-hidden relative">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#1D6FE8]/10 rounded-full blur-3xl" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C]" />
            <span className="text-sm font-body font-medium text-[#C9A84C] tracking-wide uppercase">
              {t('community.eyebrow')}
            </span>
          </div>
          <h2 className="font-display font-bold text-white leading-tight tracking-tight mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}>
            <span className="block">{t('community.headline')}</span>
            <span className="block text-[#1D6FE8]">{t('community.headline2')}</span>
            <span className="block gradient-text-gold">{t('community.headline3')}</span>
          </h2>
          <p className="font-body text-lg text-white/60 leading-relaxed">
            {t('community.subtitle')}
          </p>
        </div>

        {/* World Map SVG */}
        <div className={`relative mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="relative w-full" style={{ paddingBottom: '45%' }}>
            <svg
              viewBox="0 0 100 50"
              className="absolute inset-0 w-full h-full"
              style={{ filter: 'drop-shadow(0 0 20px rgba(29, 111, 232, 0.1))' }}
            >
              {/* Simplified world map outline */}
              <defs>
                <radialGradient id="mapGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#1D6FE8" stopOpacity="0.1" />
                  <stop offset="100%" stopColor="#1D6FE8" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Grid lines */}
              {[10, 20, 30, 40, 50, 60, 70, 80, 90]?.map(x => (
                <line key={`v${x}`} x1={x} y1="0" x2={x} y2="50" stroke="#1D6FE8" strokeWidth="0.05" strokeOpacity="0.2" />
              ))}
              {[10, 20, 30, 40]?.map(y => (
                <line key={`h${y}`} x1="0" y1={y} x2="100" y2={y} stroke="#1D6FE8" strokeWidth="0.05" strokeOpacity="0.2" />
              ))}

              {/* Connection lines between points */}
              {connectionPoints?.slice(0, 8)?.map((point, i) => {
                const next = connectionPoints?.[(i + 3) % connectionPoints?.length];
                return (
                  <line
                    key={`line${i}`}
                    x1={point?.x}
                    y1={point?.y}
                    x2={next?.x}
                    y2={next?.y}
                    stroke="#1D6FE8"
                    strokeWidth="0.08"
                    strokeOpacity="0.25"
                    strokeDasharray="0.3 0.3"
                  />
                );
              })}

              {/* Connection points */}
              {connectionPoints?.map((point, i) => (
                <g key={i}>
                  {/* Pulse ring */}
                  <circle
                    cx={point?.x}
                    cy={point?.y}
                    r="0.8"
                    fill="none"
                    stroke="#1D6FE8"
                    strokeWidth="0.1"
                    strokeOpacity="0.4"
                    style={{
                      animation: `ping 2s ease-in-out infinite`,
                      animationDelay: `${point?.delay}ms`,
                    }}
                  />
                  {/* Core dot */}
                  <circle
                    cx={point?.x}
                    cy={point?.y}
                    r="0.35"
                    fill={i % 4 === 0 ? '#C9A84C' : '#1D6FE8'}
                    opacity={isVisible ? 1 : 0}
                    style={{
                      transition: `opacity 0.5s ease ${point?.delay}ms`,
                    }}
                  />
                </g>
              ))}
            </svg>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {stats?.map((stat, i) => (
            <div
              key={stat?.key}
              className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className="text-4xl lg:text-5xl font-display font-bold text-white mb-2">
                {stat?.value}
              </div>
              <div className="text-white/50 font-body text-base">
                {t(`community.${stat?.key}`)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style jsx>{`
        @keyframes ping {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(2); opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default GlobalCommunitySection;
